import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Code, Loader2, GitCompare } from 'lucide-react';
import { apiService } from '../../services/api';

interface Props {
  workspaceId: string;
  path: string;
  conversationId: string;
  /** When the file appears as an "Edited" card, we also try to fetch a
   * baseline (the version before the edit) to render a diff view. */
  showDiff?: boolean;
  baselineContent?: string | null;
  onClose: () => void;
}

function buildLineDiff(before: string, after: string) {
  const a = before.split('\n');
  const b = after.split('\n');
  const max = Math.max(a.length, b.length);
  const rows: Array<{ kind: 'eq' | 'rm' | 'add'; left?: string; right?: string }> = [];
  for (let i = 0; i < max; i++) {
    const la = a[i]; const lb = b[i];
    if (la === lb) rows.push({ kind: 'eq', left: la, right: lb });
    else if (la !== undefined && lb !== undefined) {
      rows.push({ kind: 'rm', left: la });
      rows.push({ kind: 'add', right: lb });
    } else if (la !== undefined) rows.push({ kind: 'rm', left: la });
    else if (lb !== undefined) rows.push({ kind: 'add', right: lb });
  }
  return rows;
}

export const FilePreviewModal: React.FC<Props> = ({
  workspaceId, path, showDiff, baselineContent, onClose,
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'file' | 'diff'>(showDiff ? 'diff' : 'file');

  useEffect(() => {
    apiService.getWorkspaceFile(workspaceId, path)
      .then((d) => setContent(d.content))
      .catch((e) => setErr(e?.response?.data?.detail || 'Error al cargar'));
  }, [workspaceId, path]);

  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const diffRows = useMemo(() => {
    if (mode !== 'diff' || content === null || baselineContent === undefined || baselineContent === null) return null;
    return buildLineDiff(baselineContent, content);
  }, [mode, content, baselineContent]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 sm:p-4 chat-safe-bottom"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-3xl max-h-[88vh] bg-[#0B0C10] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="chat-file-preview-modal"
      >
        <div className="px-3 sm:px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Code className="w-4 h-4 text-violet-300 shrink-0" />
            <span className="text-sm font-mono text-zinc-100 truncate">{path}</span>
          </div>
          {baselineContent !== undefined && baselineContent !== null && (
            <div className="flex rounded-full bg-white/5 border border-white/10 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setMode('file')}
                className={`px-2.5 py-1 rounded-full transition-colors ${
                  mode === 'file' ? 'bg-violet-500/30 text-violet-100' : 'text-zinc-400'
                }`}
                data-testid="diff-mode-file"
              >Archivo</button>
              <button
                type="button"
                onClick={() => setMode('diff')}
                className={`px-2.5 py-1 rounded-full transition-colors inline-flex items-center gap-1 ${
                  mode === 'diff' ? 'bg-violet-500/30 text-violet-100' : 'text-zinc-400'
                }`}
                data-testid="diff-mode-diff"
              >
                <GitCompare className="w-3 h-3" /> Diff
              </button>
            </div>
          )}
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="chat-file-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-3 sm:p-4 font-mono text-xs text-zinc-200 whitespace-pre">
          {err && <div className="text-rose-300">{err}</div>}
          {!err && content === null && (
            <div className="flex items-center gap-2 text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          )}
          {mode === 'file' && content !== null && content}
          {mode === 'diff' && diffRows && (
            <div className="space-y-px">
              {diffRows.map((r, i) => (
                <div key={i} className={
                  r.kind === 'eq' ? 'text-zinc-500' :
                  r.kind === 'rm' ? 'text-rose-300 bg-rose-500/10 px-1' :
                  'text-emerald-300 bg-emerald-500/10 px-1'
                }>
                  <span className="opacity-50 mr-2 select-none">
                    {r.kind === 'eq' ? '  ' : r.kind === 'rm' ? '- ' : '+ '}
                  </span>
                  {r.left ?? r.right ?? ''}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
